import React, { Fragment, useReducer, useEffect, useMemo, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams, Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Paper from "@mui/material/Paper";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import moment from "moment";

import ai from "../../helpers/axios";
import PageHeaderRow from "../shared/page-header-row";
import Loader from "../loader";
import "./click-to-fly-results.css";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: true,
      };
    }
    case "dataDownloaded": {
      const result = action.payload.data;
      const originalDate = result?.estimateId?.departureDate;
      
      // Initialize selections for each aircraft
      const aircraftSelections = {};
      result?.aircraftOptions?.forEach((aircraft) => {
        const aircraftId = aircraft.aircraftTypeId?._id || aircraft.aircraftTypeId;
        aircraftSelections[aircraftId] = {
          // Date range selection (start with exact date = original date only)
          dateRangeStart: originalDate ? moment(originalDate).startOf('day').toDate() : null,
          dateRangeEnd: originalDate ? moment(originalDate).startOf('day').toDate() : null,
          airportRadius: 0,
          yomYear: aircraft.toggleOptions?.yom?.options?.find(o => o.isDefault)?.year || null,
          // Per-leg departure time selections (OW uses leg1 only, RT uses both)
          leg1DepartureTimeType: "exact",
          leg1DepartureTimeWindow: null,
          leg2DepartureTimeType: "exact",
          leg2DepartureTimeWindow: null,
          // WiFi add-on selection
          wifiEnabled: false,
        };
      });
      
      return {
        ...state,
        result: result,
        loading: false,
        aircraftSelections,
      };
    }
    case "updateAircraftSelection": {
      const { aircraftId, field, value } = action.payload;
      return {
        ...state,
        aircraftSelections: {
          ...state.aircraftSelections,
          [aircraftId]: {
            ...state.aircraftSelections[aircraftId],
            [field]: value,
          },
        },
      };
    }
    case "setDateRange": {
      const { aircraftId, startDate, endDate } = action.payload;
      return {
        ...state,
        aircraftSelections: {
          ...state.aircraftSelections,
          [aircraftId]: {
            ...state.aircraftSelections[aircraftId],
            dateRangeStart: startDate,
            dateRangeEnd: endDate,
          },
        },
      };
    }
    case "savingSelection": {
      return {
        ...state,
        saving: true,
        savingAircraftId: action.payload,
      };
    }
    case "selectionSaved": {
      return {
        ...state,
        result: action.payload.data,
        saving: false,
        savingAircraftId: null,
      };
    }
    case "saveFailed": {
      return {
        ...state,
        saving: false,
        savingAircraftId: null,
      };
    }
    default:
      break;
  }
  return state;
};

const formatCurrency = (value) => {
  if (value == null) return "—";
  return "$" + value.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

const formatPercent = (value) => {
  if (value == null) return "—";
  return (value * 100).toFixed(1) + "%";
};

const formatTripType = (tripType) => {
  switch (tripType) {
    case 1:
      return "One Way";
    case 2:
      return "Round Trip";
    default:
      return "—";
  }
};

const formatPricingMethod = (method) => {
  switch (method) {
    case "ow":
      return "OW Pricing";
    case "ptp2":
      return "PTP2 Pricing";
    case "otg":
      return "OTG Pricing";
    case "drt":
      return "DRT Pricing";
    default:
      return method || "—";
  }
};

const formatSelectionOrigin = (origin) => {
  switch (origin) {
    case "hqCalculator":
      return "HQ Calculator";
    case "hqSingleCTFPage":
      return "HQ CTF Page";
    case "iosApp":
      return "iOS App";
    default:
      return origin || "—";
  }
};

const formatEstimateOrigin = (origin) => {
  switch (origin) {
    case "flightDeckInternal":
      return "HQ - Internal";
    case "flightDeckExternal":
      return "HQ - External";
    case "liveAPI":
      return "Live API";
    case "ios":
      return "iOS App";
    case "outsideOrg":
      return "Outside Org";
    case "atc":
      return "ATC";
    case "testEstimate":
      return "Test Estimate";
    default:
      return origin || "—";
  }
};

const formatDateRange = (dateRange) => {
  if (!dateRange) return "—";
  const earliest = dateRange.earliest ? moment(dateRange.earliest).format("MMM D") : "?";
  const latest = dateRange.latest ? moment(dateRange.latest).format("MMM D, YYYY") : "?";
  if (earliest === latest || dateRange.earliest === dateRange.latest) {
    return moment(dateRange.originalDate || dateRange.earliest).format("MMM D, YYYY");
  }
  return `${earliest} - ${latest}`;
};

// Calculate TDR days from date range (number of days in window)
const calculateTdrDaysFromRange = (startDate, endDate) => {
  if (!startDate || !endDate) return 1;
  
  const start = moment(startDate).startOf('day');
  const end = moment(endDate).startOf('day');
  
  // TDR days = number of days in the range (1, 2, or 3)
  return Math.min(end.diff(start, 'days') + 1, 3);
};

// Calculate price for a specific aircraft with given selections
const calculatePriceForAircraft = (aircraft, selections) => {
  if (!aircraft || !selections) return null;

  let price = aircraft.defaultPrice;
  let totalDiscountPercent = 0;

  // TDR discount based on date range
  if (selections.dateRangeStart && selections.dateRangeEnd && aircraft.toggleOptions?.departureTdr?.available) {
    const tdrDays = calculateTdrDaysFromRange(selections.dateRangeStart, selections.dateRangeEnd);
    const tdrOption = aircraft.toggleOptions.departureTdr.options?.find(
      o => o.tdrDays === tdrDays
    );
    if (tdrOption?.change?.percent) {
      totalDiscountPercent += Math.abs(tdrOption.change.percent);
    }
  }

  // Airport radius discount
  if (selections.airportRadius && aircraft.toggleOptions?.alternateAirports?.options) {
    const airportOption = aircraft.toggleOptions.alternateAirports.options.find(
      o => o.radius === selections.airportRadius
    );
    if (airportOption?.change?.percent) {
      totalDiscountPercent += Math.abs(airportOption.change.percent);
    }
  }

  // YOM adjustment
  if (selections.yomYear && aircraft.toggleOptions?.yom?.options) {
    const yomOption = aircraft.toggleOptions.yom.options.find(
      o => o.year === selections.yomYear
    );
    if (yomOption?.change?.percent) {
      if (yomOption.isDiscount) {
        totalDiscountPercent += Math.abs(yomOption.change.percent);
      } else if (yomOption.isIncrease) {
        totalDiscountPercent -= yomOption.change.percent;
      }
    }
  }

  // Departure time discount (per-leg: OW uses leg1 only, RT uses both)
  const departureTimeOptions = aircraft.toggleOptions?.departureTime;
  const isRoundTrip = departureTimeOptions?.tripType === 2;
  
  if (isRoundTrip) {
    // Round trip: check both legs
    if (selections.leg1DepartureTimeType === "flexible" && departureTimeOptions?.leg1Options) {
      const leg1Option = departureTimeOptions.leg1Options.find(
        o => o.type === "flexible" && 
             o.timeWindow?.startTime === selections.leg1DepartureTimeWindow?.startTime
      );
      if (leg1Option?.change?.percent) {
        totalDiscountPercent += Math.abs(leg1Option.change.percent);
      }
    }
    if (selections.leg2DepartureTimeType === "flexible" && departureTimeOptions?.leg2Options) {
      const leg2Option = departureTimeOptions.leg2Options.find(
        o => o.type === "flexible" && 
             o.timeWindow?.startTime === selections.leg2DepartureTimeWindow?.startTime
      );
      if (leg2Option?.change?.percent) {
        totalDiscountPercent += Math.abs(leg2Option.change.percent);
      }
    }
  } else {
    // One-way: use leg1 with options array
    if (selections.leg1DepartureTimeType === "flexible" && departureTimeOptions?.options) {
      const timeOption = departureTimeOptions.options.find(
        o => o.type === "flexible" && 
             o.timeWindow?.startTime === selections.leg1DepartureTimeWindow?.startTime
      );
      if (timeOption?.change?.percent) {
        totalDiscountPercent += Math.abs(timeOption.change.percent);
      }
    }
  }

  // Calculate base discounted price first
  const totalDiscountDollars = price * totalDiscountPercent;
  let finalPrice = price - totalDiscountDollars;

  // WiFi add-on cost (added after discounts are applied)
  let wifiAddOnDollars = 0;
  if (selections.wifiEnabled && selections.yomYear) {
    const yomOption = aircraft.toggleOptions?.yom?.options?.find(
      o => o.year === selections.yomYear
    );
    if (yomOption?.wifi?.available && yomOption?.wifi?.addOnPercent) {
      wifiAddOnDollars = price * yomOption.wifi.addOnPercent;
      finalPrice += wifiAddOnDollars;
    }
  }

  return {
    defaultPrice: price,
    totalDiscountPercent,
    totalDiscountDollars,
    wifiAddOnDollars,
    finalPrice,
  };
};

// DateRangePicker component for TDR selection
// Single-click sets range from original to clicked date (directional)
const DateRangePicker = ({ originalDate, selectedStart, selectedEnd, onRangeChange, tdrOptions }) => {
  // Generate 5 days: original ± 2 days
  const dates = useMemo(() => {
    if (!originalDate) return [];
    const original = moment(originalDate).startOf('day');
    return [-2, -1, 0, 1, 2].map(offset => ({
      date: original.clone().add(offset, 'days').toDate(),
      offset, // Raw offset: -2, -1, 0, 1, 2
      isOriginal: offset === 0,
      dayName: original.clone().add(offset, 'days').format('ddd'),
      dayNum: original.clone().add(offset, 'days').format('D'),
      month: original.clone().add(offset, 'days').format('MMM'),
    }));
  }, [originalDate]);

  // Calculate TDR days from current selection (number of days in range)
  const tdrDays = useMemo(() => {
    if (!selectedStart || !selectedEnd) return 1;
    const start = moment(selectedStart).startOf('day');
    const end = moment(selectedEnd).startOf('day');
    return end.diff(start, 'days') + 1; // 1, 2, or 3 days
  }, [selectedStart, selectedEnd]);

  // Get discount for current TDR days
  const currentDiscount = useMemo(() => {
    if (!tdrOptions) return null;
    return tdrOptions.find(o => o.tdrDays === tdrDays);
  }, [tdrOptions, tdrDays]);

  // Single click: set range from original to clicked date
  const handleDateClick = (dateInfo) => {
    const original = moment(originalDate).startOf('day');
    const clicked = moment(dateInfo.date).startOf('day');
    
    if (dateInfo.isOriginal) {
      // Clicked original = exact date
      onRangeChange(original.toDate(), original.toDate());
    } else if (dateInfo.offset < 0) {
      // Clicked before original: range is clicked -> original
      onRangeChange(clicked.toDate(), original.toDate());
    } else {
      // Clicked after original: range is original -> clicked
      onRangeChange(original.toDate(), clicked.toDate());
    }
  };

  const isInRange = (dateInfo) => {
    if (!selectedStart || !selectedEnd) return false;
    const d = moment(dateInfo.date).startOf('day');
    const start = moment(selectedStart).startOf('day');
    const end = moment(selectedEnd).startOf('day');
    return d.isSameOrAfter(start) && d.isSameOrBefore(end);
  };

  const isEdge = (dateInfo) => {
    if (!selectedStart || !selectedEnd) return false;
    const d = moment(dateInfo.date).startOf('day');
    const start = moment(selectedStart).startOf('day');
    const end = moment(selectedEnd).startOf('day');
    return (d.isSame(start) || d.isSame(end)) && !start.isSame(end);
  };

  // Get label for current selection
  const getFlexLabel = () => {
    if (tdrDays === 1) return "Exact Date";
    return `${tdrDays}-Day Window`;
  };

  return (
    <div className="date-range-picker">
      <div className="date-range-header">
        <span className="picker-label">Click a date to set your flexibility window:</span>
      </div>
      <div className="date-range-days">
        {dates.map((d, i) => (
          <div
            key={i}
            className={`date-day ${isInRange(d) ? 'in-range' : ''} ${isEdge(d) ? 'is-edge' : ''} ${d.isOriginal ? 'is-original' : ''}`}
            onClick={() => handleDateClick(d)}
          >
            <span className="day-name">{d.dayName}</span>
            <span className="day-num">{d.dayNum}</span>
            <span className="day-month">{d.month}</span>
            {d.isOriginal && <span className="original-badge">Requested</span>}
          </div>
        ))}
      </div>
      <div className="date-range-summary">
        <span className="range-display">
          <strong>{getFlexLabel()}</strong>
          {tdrDays > 1 && (
            <span className="range-dates ml-10">
              ({moment(selectedStart).format('MMM D')} - {moment(selectedEnd).format('MMM D')})
            </span>
          )}
        </span>
        {currentDiscount && currentDiscount.change?.percent !== 0 && (
          <span className="range-discount text-success">
            Save {formatPercent(Math.abs(currentDiscount.change?.percent))} ({formatCurrency(Math.abs(currentDiscount.change?.dollars))})
          </span>
        )}
        {currentDiscount && currentDiscount.change?.percent === 0 && (
          <span className="range-discount text-muted">No flexibility discount</span>
        )}
      </div>
    </div>
  );
};

const SingleClickToFlyResultContent = () => {
  const { id } = useParams();
  const history = useHistory();

  const initialState = {
    result: null,
    loading: true,
    saving: false,
    savingAircraftId: null,
    aircraftSelections: {},
  };

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [state, dispatch] = useReducer(reducer, initialState);

  const getData = async () => {
    dispatch({ type: "dataBeganDownloading" });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let response = await ai
      .auth(token)
      .get(`/api/clickToFly/result/${id}`)
      .catch((error) => {
        history.push({ pathname: "/click-to-fly/results" });
        setTimeout(() => {
          toast.error("ClickToFly result not found!", {
            position: "top-right",
            autoClose: 5000,
          });
        }, 50);
        return console.error(error.response);
      });

    if (response) {
      dispatch({
        type: "dataDownloaded",
        payload: {
          data: response.data,
        },
      });
    }
  };

  useEffect(() => {
    getData();
    document.title = "ClickToFly Result | evoJets HQ";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { result } = state;
  const hasUserSelection = result?.userSelection?.selectedAt;

  const handleSaveSelection = useCallback(async (aircraft) => {
    const aircraftId = aircraft.aircraftTypeId?._id || aircraft.aircraftTypeId;
    const selections = state.aircraftSelections[aircraftId];
    const originalDate = state.result?.estimateId?.departureDate;
    
    if (!selections) {
      toast.error("Please configure options before saving");
      return;
    }

    const calculatedPrice = calculatePriceForAircraft(aircraft, selections);
    if (!calculatedPrice) {
      toast.error("Error calculating price");
      return;
    }

    dispatch({ type: "savingSelection", payload: aircraftId });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    // Calculate TDR days from user's selected date range
    const tdrDays = calculateTdrDaysFromRange(selections.dateRangeStart, selections.dateRangeEnd);
    
    // Build selected date range from user's actual selection
    const selectedDateRange = {
      earliest: selections.dateRangeStart,
      latest: selections.dateRangeEnd,
      originalDate: originalDate,
    };

    // Get alternate airports
    const airportOption = aircraft.toggleOptions?.alternateAirports?.options?.find(
      o => o.radius === selections.airportRadius
    );

    const selectionData = {
      selectedAircraftTypeId: aircraftId,
      selectedAircraftTypeName: aircraft.aircraftTypeName,
      selectedTdrDays: tdrDays,
      selectedDateRange: selectedDateRange,
      selectedAirportRadius: selections.airportRadius,
      selectedDepartureAirports: airportOption?.departureAlternates || [],
      selectedArrivalAirports: airportOption?.arrivalAlternates || [],
      selectedYomYear: selections.yomYear,
      // Per-leg departure time selections
      selectedLeg1DepartureTimeType: selections.leg1DepartureTimeType,
      selectedLeg1DepartureTimeWindow: selections.leg1DepartureTimeWindow,
      selectedLeg2DepartureTimeType: selections.leg2DepartureTimeType,
      selectedLeg2DepartureTimeWindow: selections.leg2DepartureTimeWindow,
      // WiFi add-on selection
      selectedWifiEnabled: selections.wifiEnabled || false,
      finalCalculatedPrice: calculatedPrice.finalPrice,
      totalDiscountPercent: calculatedPrice.totalDiscountPercent,
      totalDiscountDollars: calculatedPrice.totalDiscountDollars,
      selectionOrigin: "hqSingleCTFPage",
    };

    try {
      const response = await ai
        .auth(token)
        .post(`/api/clickToFly/result/${id}/selection`, selectionData);

      dispatch({
        type: "selectionSaved",
        payload: { data: response.data },
      });

      toast.success("Preferences saved successfully!", {
        position: "top-right",
        autoClose: 5000,
      });
    } catch (error) {
      dispatch({ type: "saveFailed" });
      const errorMsg = getApiErrorMessage(error, "Failed to save preferences");
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
      console.error("Error saving selection:", error);
    }
  }, [state.aircraftSelections, state.result?.estimateId?.departureDate, getAccessTokenSilently, loginWithRedirect, id]);

  if (state.loading) {
    return <Loader />;
  }

  const routeDisplay = result?.routeId
    ? `${result.routeId.departureAirport?.code || "?"} → ${
        result.routeId.arrivalAirport?.code || "?"
      }`
    : "—";

  return (
    <Fragment>
      <PageHeaderRow
        h1={`ClickToFly Result`}
        pretitle="Business"
        breadcrumbs={[
          { link: "/click-to-fly", label: "ClickToFly Hub" },
          { link: "/click-to-fly/results", label: "Results" },
        ]}
      />

      {/* Header Section */}
      <Paper className="ctf-result-header p-20 mb-20">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-20">
          <div>
            <h2 className="mb-10">{routeDisplay}</h2>
            <div className="d-flex gap-20 flex-wrap">
              <span className="badge bg-primary">
                {formatTripType(result?.tripType)}
              </span>
              <span className="badge bg-secondary">
                {formatPricingMethod(result?.lowestPriceMethod)}
              </span>
              {hasUserSelection && (
                <span className="badge bg-success">
                  Preferences Saved
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="mb-5">
              <strong>Calculated:</strong>{" "}
              {moment(result?.calculatedAt).format("MMM D, YYYY h:mm A")}
            </p>
            <p className="mb-0">
              <strong>ID:</strong> <code>{result?._id}</code>
            </p>
          </div>
        </div>
      </Paper>

      {/* Saved Preferences Section - Show if userSelection exists */}
      {hasUserSelection && (
        <Paper className="ctf-result-section p-20 mb-20" style={{ borderLeft: "4px solid #28a745" }}>
          <h4 className="section-heading mb-15" style={{ color: "#28a745" }}>
            <span className="material-icons mr-10" style={{ verticalAlign: "middle" }}>check_circle</span>
            Saved Preferences
          </h4>
          <div className="info-grid">
            <div className="info-item highlight">
              <label>Final Price</label>
              <span className="large-value" style={{ color: "#28a745" }}>
                {formatCurrency(result.userSelection.finalCalculatedPrice)}
              </span>
            </div>
            <div className="info-item">
              <label>Selected Aircraft</label>
              <span>{result.userSelection.selectedAircraftTypeName || "—"}</span>
            </div>
            <div className="info-item">
              <label>Total Discount</label>
              <span className="text-success">
                {formatPercent(result.userSelection.totalDiscountPercent)} ({formatCurrency(result.userSelection.totalDiscountDollars)})
              </span>
            </div>
            <div className="info-item">
              <label>Date Flexibility</label>
              <span>
                {result.userSelection.selectedTdrDays === 1
                  ? "Exact Date"
                  : `±${result.userSelection.selectedTdrDays - 1} days`}
                {result.userSelection.selectedDateRange && (
                  <span className="text-muted ml-5">
                    ({formatDateRange(result.userSelection.selectedDateRange)})
                  </span>
                )}
              </span>
            </div>
            <div className="info-item">
              <label>Airport Radius</label>
              <span>
                {result.userSelection.selectedAirportRadius === 0
                  ? "Exact Airports"
                  : `Within ${result.userSelection.selectedAirportRadius} miles`}
              </span>
            </div>
            <div className="info-item">
              <label>Year of Manufacture</label>
              <span>{result.userSelection.selectedYomYear ? `${result.userSelection.selectedYomYear}+` : "—"}</span>
            </div>
            {/* Departure Time - Per-leg display */}
            {result.tripType === 2 ? (
              <>
                <div className="info-item">
                  <label>Departure Time - Leg 1</label>
                  <span>
                    {result.userSelection.selectedLeg1DepartureTimeType === "exact"
                      ? "Exact Time"
                      : result.userSelection.selectedLeg1DepartureTimeWindow
                        ? `${result.userSelection.selectedLeg1DepartureTimeWindow.startLabel} - ${result.userSelection.selectedLeg1DepartureTimeWindow.endLabel}`
                        : "Flexible"}
                  </span>
                </div>
                <div className="info-item">
                  <label>Departure Time - Leg 2</label>
                  <span>
                    {result.userSelection.selectedLeg2DepartureTimeType === "exact"
                      ? "Exact Time"
                      : result.userSelection.selectedLeg2DepartureTimeWindow
                        ? `${result.userSelection.selectedLeg2DepartureTimeWindow.startLabel} - ${result.userSelection.selectedLeg2DepartureTimeWindow.endLabel}`
                        : "Flexible"}
                  </span>
                </div>
              </>
            ) : (
              <div className="info-item">
                <label>Departure Time</label>
                <span>
                  {result.userSelection.selectedLeg1DepartureTimeType === "exact"
                    ? "Exact Time"
                    : result.userSelection.selectedLeg1DepartureTimeWindow
                      ? `${result.userSelection.selectedLeg1DepartureTimeWindow.startLabel} - ${result.userSelection.selectedLeg1DepartureTimeWindow.endLabel}`
                      : "Flexible"}
                </span>
              </div>
            )}
            <div className="info-item">
              <label>WiFi</label>
              <span>
                {result.userSelection.selectedWifiEnabled
                  ? "Included"
                  : "Not selected"}
              </span>
            </div>
            <div className="info-item">
              <label>Saved From</label>
              <span>{formatSelectionOrigin(result.userSelection.selectionOrigin)}</span>
            </div>
            <div className="info-item">
              <label>Saved At</label>
              <span>{moment(result.userSelection.selectedAt).format("MMM D, YYYY h:mm A")}</span>
            </div>
          </div>
        </Paper>
      )}

      {/* Estimate Summary Section */}
      <Paper className="ctf-result-section p-20 mb-20">
        <h4 className="section-heading mb-15">
          Estimate Summary
          {result?.estimateId?._id && (
            <Link
              to={`/estimates/${result.estimateId._id}`}
              className="ml-10 base-button evo-blue icon-button icon-button-small"
              title="View Full Estimate"
            >
              <span className="material-icons">open_in_new</span>
            </Link>
          )}
        </h4>
        <div className="info-grid">
          <div className="info-item">
            <label>Departure Airport</label>
            <span>
              {result?.estimateId?.departureAirportCode || "—"} -{" "}
              {result?.estimateId?.departureAirportName || ""}
            </span>
          </div>
          <div className="info-item">
            <label>Arrival Airport</label>
            <span>
              {result?.estimateId?.arrivalAirportCode || "—"} -{" "}
              {result?.estimateId?.arrivalAirportName || ""}
            </span>
          </div>
          <div className="info-item">
            <label>Departure Date</label>
            <span>
              {result?.estimateId?.departureDate
                ? moment(result.estimateId.departureDate).format("MM/DD/YYYY")
                : "—"}
            </span>
          </div>
          <div className="info-item">
            <label>Return Date</label>
            <span>
              {result?.estimateId?.returnDate
                ? moment(result.estimateId.returnDate).format("MM/DD/YYYY")
                : "—"}
            </span>
          </div>
          <div className="info-item">
            <label>Passengers</label>
            <span>{result?.estimateId?.passengerCount || "—"}</span>
          </div>
          <div className="info-item">
            <label>Distance</label>
            <span>
              {result?.distance
                ? `${result.distance.toLocaleString()} miles`
                : "—"}
            </span>
          </div>
          <div className="info-item">
            <label>Origin</label>
            <span>{formatEstimateOrigin(result?.estimateId?.origin)}</span>
          </div>
        </div>
      </Paper>

      {/* Pricing Overview Section */}
      <Paper className="ctf-result-section p-20 mb-20">
        <h4 className="section-heading mb-15">Pricing Overview</h4>
        <div className="info-grid">
          <div className="info-item highlight">
            <label>Lowest Starting Price</label>
            <span className="large-value">
              {formatCurrency(result?.lowestStartingPrice)}
            </span>
          </div>
          <div className="info-item">
            <label>Best Aircraft Option</label>
            <span>{result?.lowestStartingAircraftTypeId?.name || "—"}</span>
          </div>
          <div className="info-item">
            <label>Pricing Method</label>
            <span>{formatPricingMethod(result?.lowestPriceMethod)}</span>
          </div>
          <div className="info-item">
            <label>Price Range Used</label>
            <span>
              {result?.pricingConfigSnapshot?.priceRangeUsed
                ? `$${result.pricingConfigSnapshot.priceRangeUsed.minPrice?.toLocaleString()} - $${result.pricingConfigSnapshot.priceRangeUsed.maxPrice?.toLocaleString()}`
                : "—"}
            </span>
          </div>
        </div>
      </Paper>

      {/* Aircraft Options Section */}
      <Paper className="ctf-result-section p-20 mb-20">
        <h4 className="section-heading mb-15">
          Aircraft Options
          {!hasUserSelection && (
            <span className="text-muted ml-10" style={{ fontSize: "0.8rem", textTransform: "none", fontFamily: "ProximaNova-Regular" }}>
              — Select options and save preferences for any aircraft
            </span>
          )}
        </h4>
        {result?.aircraftOptions
          ?.slice()
          .sort((a, b) => a.defaultPrice - b.defaultPrice)
          .map((aircraft, index) => {
            const aircraftId = aircraft.aircraftTypeId?._id || aircraft.aircraftTypeId;
            const selections = state.aircraftSelections[aircraftId] || {};
            const calculatedPrice = calculatePriceForAircraft(aircraft, selections);
            const isSelectedAircraft = hasUserSelection && 
              (result.userSelection.selectedAircraftTypeId === aircraftId ||
               result.userSelection.selectedAircraftTypeId?._id === aircraftId);
            
            return (
              <Accordion 
                key={aircraftId || index} 
                defaultExpanded={index === 0}
                className={isSelectedAircraft ? "selected-aircraft" : ""}
              >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <div className="d-flex justify-content-between w-100 pr-20 align-items-center">
                    <div className="d-flex align-items-center gap-10">
                      {isSelectedAircraft && (
                        <span className="material-icons text-success" style={{ fontSize: "20px" }}>check_circle</span>
                      )}
                <strong>{aircraft.aircraftTypeName}</strong>
                    </div>
                <div className="d-flex gap-15 align-items-center">
                  <span>
                    Default: {formatCurrency(aircraft.defaultPrice)}
                  </span>
                  <span className="text-success">
                    Max Discount: {formatPercent(aircraft.theoreticalMaxDiscount?.percent)}
                  </span>
                  <span className="text-primary">
                    Low as: {formatCurrency(aircraft.theoreticalMaxDiscount?.lowestPossiblePrice)}
                  </span>
                </div>
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <div className="aircraft-option-details">
                    {/* Basic Info */}
                <div className="info-grid mb-20">
                  <div className="info-item">
                    <label>Seats</label>
                    <span>
                      {aircraft.seatCount ? aircraft.seatCount : "—"}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Estimate Final Price</label>
                    <span>{formatCurrency(aircraft.estimateFinalPrice)}</span>
                  </div>
                  <div className="info-item">
                    <label>Default Price</label>
                    <span>{formatCurrency(aircraft.defaultPrice)}</span>
                  </div>
                  <div className="info-item">
                    <label>Enabled for Route</label>
                    <span>{aircraft.isEnabledForRoute ? "Yes" : "No"}</span>
                  </div>
                </div>

                    {/* Member Discount Info (read-only) */}
                {aircraft.toggleOptions?.member && (
                  <div className="toggle-option-section mb-15">
                        <h6>Member Status</h6>
                    <div className="toggle-option-grid">
                      <span>
                        Is Member: {aircraft.toggleOptions.member.isMember ? "Yes" : "No"}
                      </span>
                      <span>
                        Member Rate: {formatPercent(Math.abs(aircraft.toggleOptions.member.memberDiscount?.percent))}
                      </span>
                      <span>
                        Non-Member Rate: {formatPercent(Math.abs(aircraft.toggleOptions.member.nonMemberDiscount?.percent))}
                      </span>
                    </div>
                  </div>
                )}

                    {/* Interactive Selection Section - only if no saved selection */}
                    {!hasUserSelection && (
                      <div className="selection-section mt-20">
                        <h5 className="mb-15" style={{ color: "var(--ctf-primary)" }}>
                          Configure Preferences
                        </h5>

                        {/* TDR Date Flexibility */}
                {aircraft.toggleOptions?.departureTdr?.available && (
                          <div className="tdr-section mb-15">
                            <FormLabel component="legend" className="mb-10">Date Flexibility</FormLabel>
                            <DateRangePicker
                              originalDate={result?.estimateId?.departureDate}
                              selectedStart={selections.dateRangeStart}
                              selectedEnd={selections.dateRangeEnd}
                              onRangeChange={(start, end) => dispatch({
                                type: "setDateRange",
                                payload: { aircraftId, startDate: start, endDate: end }
                              })}
                              tdrOptions={aircraft.toggleOptions.departureTdr.options}
                            />
                          </div>
                        )}

                        {/* Airport Radius */}
                        {aircraft.toggleOptions?.alternateAirports?.available && (
                          <FormControl component="fieldset" className="mb-15 w-100">
                            <FormLabel component="legend">
                              {aircraft.toggleOptions?.alternateAirports?.flexibleAirportEnd === "arrival"
                                ? "Arrival Airport Flexibility"
                                : "Departure Airport Flexibility"}
                            </FormLabel>
                            <RadioGroup
                              value={selections.airportRadius?.toString() || "0"}
                              onChange={(e) => dispatch({
                                type: "updateAircraftSelection",
                                payload: { aircraftId, field: "airportRadius", value: parseInt(e.target.value) }
                              })}
                            >
                              {aircraft.toggleOptions.alternateAirports.options?.map((opt) => (
                                <FormControlLabel
                                  key={opt.radius}
                                  value={opt.radius.toString()}
                                  control={<Radio size="small" />}
                                  label={
                                    <span>
                                      {opt.label}
                                      {opt.change?.percent !== 0 && (
                                        <span className="text-success ml-10">
                                          Save {formatPercent(Math.abs(opt.change?.percent))} ({formatCurrency(Math.abs(opt.change?.dollars))})
                                        </span>
                                      )}
                                    </span>
                                  }
                                />
                              ))}
                            </RadioGroup>
                          </FormControl>
                        )}

                        {/* YOM */}
                        {aircraft.toggleOptions?.yom?.options?.length > 0 && (
                          <FormControl component="fieldset" className="mb-15 w-100">
                            <FormLabel component="legend">Year of Manufacture</FormLabel>
                            <RadioGroup
                              value={selections.yomYear?.toString() || ""}
                              onChange={(e) => dispatch({
                                type: "updateAircraftSelection",
                                payload: { aircraftId, field: "yomYear", value: parseInt(e.target.value) }
                              })}
                            >
                              {aircraft.toggleOptions.yom.options.map((opt) => (
                                <FormControlLabel
                                  key={opt.year}
                                  value={opt.year.toString()}
                                  control={<Radio size="small" />}
                                  label={
                                    <span>
                                      {opt.year}+ ({opt.commonlyAvailable})
                                      {opt.isDefault && <span className="text-muted ml-5">(Default)</span>}
                                      {opt.isDiscount && (
                                        <span className="text-success ml-10">
                                          Save {formatPercent(Math.abs(opt.change?.percent))} ({formatCurrency(Math.abs(opt.change?.dollars))})
                                        </span>
                                      )}
                                      {opt.isIncrease && (
                                        <span className="text-danger ml-10">
                                          +{formatPercent(opt.change?.percent)} (+{formatCurrency(opt.change?.dollars)})
                                        </span>
                                      )}
                                    </span>
                                  }
                                />
                              ))}
                            </RadioGroup>
                          </FormControl>
                        )}

                        {/* WiFi Add-On - show if selected YOM has WiFi available */}
                        {(() => {
                          const selectedYomOption = aircraft.toggleOptions?.yom?.options?.find(
                            (o) => o.year === selections.yomYear
                          );
                          if (selectedYomOption?.wifi?.available) {
                            return (
                              <FormControl component="fieldset" className="mb-15 w-100">
                                <FormLabel component="legend">WiFi Add-On</FormLabel>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selections.wifiEnabled || false}
                                      onChange={(e) =>
                                        dispatch({
                                          type: "updateAircraftSelection",
                                          payload: {
                                            aircraftId,
                                            field: "wifiEnabled",
                                            value: e.target.checked,
                                          },
                                        })
                                      }
                                    />
                                  }
                                  label={
                                    <span>
                                      Add WiFi
                                      <span className="text-danger ml-10">
                                        +{formatPercent(selectedYomOption.wifi.addOnPercent)} (+
                                        {formatCurrency(selectedYomOption.wifi.addOnDollars)})
                                      </span>
                                    </span>
                                  }
                                />
                              </FormControl>
                            );
                          }
                          return null;
                        })()}

                        {/* Departure Time - Per-leg for round trips */}
                        {(() => {
                          const departureTimeOpts = aircraft.toggleOptions?.departureTime;
                          const isRoundTrip = departureTimeOpts?.tripType === 2;
                          
                          // Helper to render departure time radio group
                          const renderDepartureTimeGroup = (options, label, fieldType, fieldWindow, selectedType, selectedWindow) => (
                            options?.length > 0 && (
                              <FormControl component="fieldset" className="mb-15 w-100">
                                <FormLabel component="legend">{label}</FormLabel>
                                <RadioGroup
                                  value={
                                    selectedType === "exact"
                                      ? "exact"
                                      : `flexible-${selectedWindow?.startTime}`
                                  }
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === "exact") {
                                      dispatch({
                                        type: "updateAircraftSelection",
                                        payload: { aircraftId, field: fieldType, value: "exact" }
                                      });
                                      dispatch({
                                        type: "updateAircraftSelection",
                                        payload: { aircraftId, field: fieldWindow, value: null }
                                      });
                                    } else {
                                      const startTime = parseInt(value.replace("flexible-", ""));
                                      const timeOption = options.find(
                                        o => o.type === "flexible" && o.timeWindow?.startTime === startTime
                                      );
                                      dispatch({
                                        type: "updateAircraftSelection",
                                        payload: { aircraftId, field: fieldType, value: "flexible" }
                                      });
                                      dispatch({
                                        type: "updateAircraftSelection",
                                        payload: { aircraftId, field: fieldWindow, value: timeOption?.timeWindow }
                                      });
                                    }
                                  }}
                                >
                                  {options.map((opt, i) => (
                                    <FormControlLabel
                                      key={i}
                                      value={opt.type === "exact" ? "exact" : `flexible-${opt.timeWindow?.startTime}`}
                                      control={<Radio size="small" />}
                                      label={
                                        <span>
                                          {opt.label}
                                          {opt.timeWindow && ` (${opt.timeWindow.startLabel} - ${opt.timeWindow.endLabel})`}
                                          {opt.change?.percent !== 0 && (
                                            <span className="text-success ml-10">
                                              Save {formatPercent(Math.abs(opt.change?.percent))} ({formatCurrency(Math.abs(opt.change?.dollars))})
                                            </span>
                                          )}
                                        </span>
                                      }
                                    />
                                  ))}
                                </RadioGroup>
                              </FormControl>
                            )
                          );
                          
                          if (isRoundTrip) {
                            return (
                              <>
                                {renderDepartureTimeGroup(
                                  departureTimeOpts?.leg1Options,
                                  "Departure Time - Leg 1 (Outbound)",
                                  "leg1DepartureTimeType",
                                  "leg1DepartureTimeWindow",
                                  selections.leg1DepartureTimeType,
                                  selections.leg1DepartureTimeWindow
                                )}
                                {renderDepartureTimeGroup(
                                  departureTimeOpts?.leg2Options,
                                  "Departure Time - Leg 2 (Return)",
                                  "leg2DepartureTimeType",
                                  "leg2DepartureTimeWindow",
                                  selections.leg2DepartureTimeType,
                                  selections.leg2DepartureTimeWindow
                                )}
                              </>
                            );
                          } else {
                            return renderDepartureTimeGroup(
                              departureTimeOpts?.options,
                              "Departure Time",
                              "leg1DepartureTimeType",
                              "leg1DepartureTimeWindow",
                              selections.leg1DepartureTimeType,
                              selections.leg1DepartureTimeWindow
                            );
                          }
                        })()}

                        {/* Price Summary & Save Button */}
                        {calculatedPrice && (
                          <div className="price-summary-box mt-20">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-15">
                              <div>
                                <p className="mb-5">
                                  <span className="text-muted">Base:</span> {formatCurrency(calculatedPrice.defaultPrice)}
                                  <span className="text-success ml-10">
                                    −{formatPercent(calculatedPrice.totalDiscountPercent)} ({formatCurrency(calculatedPrice.totalDiscountDollars)})
                                  </span>
                                </p>
                                <p className="mb-0" style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#28a745" }}>
                                  {formatCurrency(calculatedPrice.finalPrice)}
                                </p>
                              </div>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleSaveSelection(aircraft)}
                                disabled={state.saving}
                              >
                                {state.saving && state.savingAircraftId === aircraftId
                                  ? "Saving..."
                                  : "Save This Selection"}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Read-only view when selection exists */}
                    {hasUserSelection && !isSelectedAircraft && (
                      <div className="toggle-options-summary mt-20">
                        <h5 className="mb-10">Available Discount Options</h5>
                        
                {aircraft.toggleOptions?.departureTdr?.available && (
                          <div className="toggle-option-section mb-10">
                            <h6>Date Flexibility</h6>
                    <div className="toggle-options-list">
                      {aircraft.toggleOptions.departureTdr.options?.map((opt, i) => (
                        <div key={i} className="toggle-option-item">
                                  <span className="option-label">{opt.label}</span>
                                  <span className="option-value text-success">
                            {formatPercent(Math.abs(opt.change?.percent))} off ({formatCurrency(Math.abs(opt.change?.dollars))})
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {aircraft.toggleOptions?.alternateAirports?.available && (
                          <div className="toggle-option-section mb-10">
                            <h6>
                              {aircraft.toggleOptions?.alternateAirports?.flexibleAirportEnd === "arrival"
                                ? "Arrival Airport Flexibility"
                                : "Departure Airport Flexibility"}
                            </h6>
                    <div className="toggle-options-list">
                      {aircraft.toggleOptions.alternateAirports.options?.map((opt, i) => (
                        <div key={i} className="toggle-option-item">
                          <span className="option-label">{opt.label}</span>
                                  <span className={`option-value ${opt.change?.percent !== 0 ? "text-success" : ""}`}>
                            {opt.change?.percent !== 0
                                      ? `${formatPercent(Math.abs(opt.change?.percent))} off (${formatCurrency(Math.abs(opt.change?.dollars))})`
                              : "No discount"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {aircraft.toggleOptions?.yom?.options?.length > 0 && (
                          <div className="toggle-option-section mb-10">
                            <h6>Year of Manufacture</h6>
                    <div className="toggle-options-list">
                      {aircraft.toggleOptions.yom.options.map((opt, i) => (
                        <div key={i} className="toggle-option-item">
                          <span className="option-label">
                            {opt.year}+ ({opt.commonlyAvailable})
                          </span>
                          <span className={`option-value ${opt.isDiscount ? "text-success" : opt.isIncrease ? "text-danger" : ""}`}>
                            {opt.isDiscount
                                      ? `${formatPercent(Math.abs(opt.change?.percent))} off (${formatCurrency(Math.abs(opt.change?.dollars))})`
                              : opt.isIncrease
                                      ? `+${formatPercent(opt.change?.percent)} (+${formatCurrency(opt.change?.dollars)})`
                              : "Base price"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Departure Time - Per-leg display for read-only summary */}
                        {(() => {
                          const departureTimeOpts = aircraft.toggleOptions?.departureTime;
                          const isRoundTrip = departureTimeOpts?.tripType === 2;
                          
                          const renderTimeOptions = (options, label) => (
                            options?.length > 0 && (
                              <div className="toggle-option-section mb-10">
                                <h6>{label}</h6>
                                <div className="toggle-options-list">
                                  {options.map((opt, i) => (
                                    <div key={i} className="toggle-option-item">
                                      <span className="option-label">
                                        {opt.label}
                                        {opt.timeWindow && ` (${opt.timeWindow.startLabel} - ${opt.timeWindow.endLabel})`}
                                      </span>
                                      <span className={`option-value ${opt.change?.percent !== 0 ? "text-success" : ""}`}>
                                        {opt.change?.percent !== 0
                                          ? `${formatPercent(Math.abs(opt.change?.percent))} off (${formatCurrency(Math.abs(opt.change?.dollars))})`
                                          : "No discount"}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          );
                          
                          if (isRoundTrip) {
                            return (
                              <>
                                {renderTimeOptions(departureTimeOpts?.leg1Options, "Departure Time - Leg 1 (Outbound)")}
                                {renderTimeOptions(departureTimeOpts?.leg2Options, "Departure Time - Leg 2 (Return)")}
                              </>
                            );
                          } else {
                            return renderTimeOptions(departureTimeOpts?.options, "Departure Time");
                          }
                        })()}
                  </div>
                )}
              </div>
            </AccordionDetails>
          </Accordion>
            );
          })}
      </Paper>

      {/* Pricing Config Snapshot Section */}
      <Paper className="ctf-result-section p-20 mb-20">
        <h4 className="section-heading mb-15">Pricing Configuration Snapshot</h4>
        <p className="text-muted mb-15">
          These values were captured at the time of calculation:{" "}
          {result?.pricingConfigSnapshot?.capturedAt
            ? moment(result.pricingConfigSnapshot.capturedAt).format("MMM D, YYYY h:mm A")
            : "—"}
        </p>
        <div className="info-grid">
          <div className="info-item">
            <label>Member Discount Rate</label>
            <span>{formatPercent(result?.pricingConfigSnapshot?.memberDiscountRate)}</span>
          </div>
          <div className="info-item">
            <label>Non-Member Discount Rate</label>
            <span>{formatPercent(result?.pricingConfigSnapshot?.nonMemberDiscountRate)}</span>
          </div>
          {result?.pricingConfigSnapshot?.priceRangeUsed && (
            <>
              <div className="info-item">
                <label>OW/PTP2/DRT Adjustment</label>
                <span>
                  {result.pricingConfigSnapshot.priceRangeUsed.owPpt2DrtAdjustment
                    ? `${(result.pricingConfigSnapshot.priceRangeUsed.owPpt2DrtAdjustment * 100).toFixed(1)}%`
                    : "—"}
                </span>
              </div>
              <div className="info-item">
                <label>OTG Adjustment</label>
                <span>
                  {result.pricingConfigSnapshot.priceRangeUsed.otgAdjustment
                    ? `${(result.pricingConfigSnapshot.priceRangeUsed.otgAdjustment * 100).toFixed(1)}%`
                    : "—"}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Time Adjustment Factors */}
        {result?.pricingConfigSnapshot?.timeAdjustmentFactors?.length > 0 && (
          <div className="mt-20">
            <h5 className="mb-10">Time Adjustment Factors</h5>
            <div className="toggle-options-list">
              {result.pricingConfigSnapshot.timeAdjustmentFactors.map((factor, i) => (
                <div key={i} className="toggle-option-item">
                  <span className="option-label">{factor.label}</span>
                  <span className="option-value">
                    {factor.startTime}:00 - {factor.endTime}:00 ({formatPercent(Math.abs(factor.adjustment))} off)
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TDR Flex Discounts */}
        {result?.pricingConfigSnapshot?.departureTdrFlexDiscounts?.length > 0 && (
          <div className="mt-20">
            <h5 className="mb-10">TDR Flex Discounts</h5>
            <div className="toggle-options-list">
              {result.pricingConfigSnapshot.departureTdrFlexDiscounts.map((discount, i) => (
                <div key={i} className="toggle-option-item">
                  <span className="option-label">{discount.tdrDays} day(s) flexibility</span>
                  <span className="option-value">{formatPercent(discount.discount)} off</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Paper>

      {/* Placeholder: Related Leads Section */}
      <Paper className="ctf-result-section p-20 mb-20">
        <h4 className="section-heading mb-15">Related Leads</h4>
        <p className="text-muted">
          Lead associations coming soon. This section will show leads that were created from this ClickToFly result.
        </p>
      </Paper>

      {/* Placeholder: Related Trips Section */}
      <Paper className="ctf-result-section p-20 mb-20">
        <h4 className="section-heading mb-15">Related Trips</h4>
        <p className="text-muted">
          Trip associations coming soon. This section will show trips that originated from this ClickToFly result.
        </p>
      </Paper>

      {/* Back Button */}
      <div className="text-center mt-20 mb-40">
        <Link to="/click-to-fly/results" className="base-button evo-gunmetal">
          Back to Results
        </Link>
      </div>
    </Fragment>
  );
};

export default SingleClickToFlyResultContent;
